import { useCallback, useEffect, useId, useMemo, useState } from "react";
import {
    Button,
    Checkbox,
    Collapse,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    SvgIcon,
    Tab,
    Tabs,
    TextField,
    Tooltip
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

import "./style.scss";
import { ReactComponent as DeleteIcon } from "../../../assets/delete.icon.svg";
import { ReactComponent as EditIcon } from "../../../assets/edit.icon.svg";
import { ReactComponent as PlusIcon } from "../../../assets/plus.icon.svg";
import {
    createOrchestrator,
    createSpecialist,
    deleteAgent,
    fetchAgentModels,
    fetchAgents,
    fetchAgenticFlag,
    fetchFlaggedAnswers,
    setAgenticFlag,
    setAgentSpecialists,
    setKnowledgeBaseAgent,
    updateAgent
} from "../../../api/admin/agents/endpoints-referrers";
import { fetchKnowledgeBases } from "../../../api/admin/knowledge-base/endpoints-referrers";
import type {
    AdminAgent,
    AgentEffortLevel,
    AgentModelRef,
    CuratedModel,
    FlaggedAnswer
} from "../../../common/interfaces/admin/agent.interface";
import { FirebaseAuthContainer } from "../../../common/containers/auth/firebase-auth-container.util";
import HeaderLayout from "../../../components/layout/header-layout.comp";
import { LoaderView } from "../../../components/loader/loader.comp";
import { ToastContainer } from "../../../components/toast/toast.comp";
import ConfirmationPopup, {
    ConfirmationPopupType
} from "../../../components/confirmation-popup/confirmation-popup.comp";
import { ModalTemplate } from "../../../components/modal-template/modal-template.comp";
import {
    DialField,
    DialHelpProvider,
    DialHelpStrip,
    dialLabel
} from "../../../components/dial-field/dial-field.comp";

interface KbRow {
    uid: string;
    label: string;
    agentUid: string | null;
}

const PLATFORM_DEFAULT = "__platform_default__";
const PROVIDER_DEFAULT_EFFORT = "__provider_default__";

const modelKey = (m: AgentModelRef | null): string =>
    m ? `${m.provider}/${m.model}` : PLATFORM_DEFAULT;

const modelSummary = (m: AgentModelRef | null): string => {
    if (!m) return "Platform default";
    return m.effort ? `${m.provider}/${m.model} (effort ${m.effort})` : `${m.provider}/${m.model}`;
};

const EFFORT_OPTIONS: { value: AgentEffortLevel; label: string }[] = [
    { value: "none", label: "None (no reasoning)" },
    { value: "low", label: "Low" },
    { value: "medium", label: "Medium" },
    { value: "high", label: "High" }
];

const BUDGET_FIELDS: { key: string; label: string }[] = [
    { key: "maxIterations", label: "Max iterations" },
    { key: "maxTokens", label: "Max tokens / turn" },
    { key: "llmCallTimeoutMs", label: "LLM call timeout (ms)" },
    { key: "toolTimeoutMs", label: "Tool timeout (ms)" },
    { key: "turnDeadlineMs", label: "Turn deadline (ms)" },
    { key: "searchTopKDefault", label: "Search top K default" },
    { key: "historyWindow", label: "History window (turns)" }
];

const VERBOSITY_OPTIONS = [
    { value: "", label: "Standard (default)" },
    { value: "concise", label: "Concise" },
    { value: "detailed", label: "Detailed" }
];

export default function AgentsView() {
    const { user } = FirebaseAuthContainer.useContainer();
    const { showToast } = ToastContainer.useContainer();
    const companyId = user?.companyId;

    const [tab, setTab] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [agents, setAgents] = useState<AdminAgent[]>([]);
    const [models, setModels] = useState<CuratedModel[]>([]);
    const [flagEnabled, setFlagEnabled] = useState<boolean>(false);
    const [kbs, setKbs] = useState<KbRow[]>([]);
    const [flagged, setFlagged] = useState<FlaggedAnswer[]>([]);
    const [flaggedTotal, setFlaggedTotal] = useState<number>(0);
    const [expandedFlag, setExpandedFlag] = useState<number | null>(null);

    const [editTarget, setEditTarget] = useState<AdminAgent | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<AdminAgent | null>(null);
    const [deleting, setDeleting] = useState(false);
    const [specialistDialogOpen, setSpecialistDialogOpen] = useState(false);
    const [orchestratorDialogOpen, setOrchestratorDialogOpen] = useState(false);
    // No checkboxes/Action menu here: the digestion maintenance operations
    // moved to the Assistants list (assistants_agents_separation_design.md
    // §3.3), and per the DESIGN_GUIDE §4 standard a list without item-level
    // bulk actions carries no selection chrome.

    const specialists = useMemo(() => agents.filter(a => a.role === "SPECIALIST"), [agents]);

    const reload = useCallback(async () => {
        if (!companyId) return;
        setIsLoading(true);
        try {
            const [agentsRes, modelsRes, flagRes, kbsRes, flaggedRes] = await Promise.all([
                fetchAgents(companyId),
                fetchAgentModels(companyId),
                fetchAgenticFlag(companyId),
                fetchKnowledgeBases(companyId),
                fetchFlaggedAnswers(companyId)
            ]);
            const agentList: AdminAgent[] = agentsRes.data ?? [];
            setAgents(agentList);
            setModels(modelsRes.data?.curated ?? []);
            setFlagEnabled(!!flagRes.data?.enabled);
            const kbList = kbsRes.data?.items ?? kbsRes.data ?? [];
            const boundBy: Record<string, string> = {};
            for (const a of agentList) {
                for (const kb of a.boundKnowledgeBases) boundBy[kb.uid] = a.uid;
            }
            setKbs(
                kbList.map((kb: any) => ({
                    uid: kb.uid,
                    label: kb.displayName || kb.name || kb.uid,
                    agentUid: boundBy[kb.uid] ?? null
                }))
            );
            setFlagged(flaggedRes.data?.results ?? []);
            setFlaggedTotal(flaggedRes.data?.total ?? 0);
        } catch (e) {
            showToast("Failed to load agents", 3000);
        } finally {
            setIsLoading(false);
        }
    }, [companyId, showToast]);

    useEffect(() => {
        reload();
    }, [reload]);

    const onToggleFlag = async (enabled: boolean) => {
        if (!companyId) return;
        try {
            await setAgenticFlag(companyId, enabled);
            setFlagEnabled(enabled);
            showToast(
                enabled ? "Agentic chat enabled" : "Agentic chat disabled (legacy path)",
                3000
            );
        } catch {
            showToast("Failed to update the flag", 3000);
        }
    };

    const onDeleteAgent = async () => {
        if (!companyId || !deleteTarget || deleting) return;
        setDeleting(true);
        try {
            await deleteAgent(companyId, deleteTarget.uid);
            showToast("Agent deleted", 3000);
            setDeleteTarget(null);
            reload();
        } catch (e: any) {
            showToast(e?.response?.data?.message ?? "Failed to delete agent", 3000);
        } finally {
            setDeleting(false);
        }
    };

    const onRebindKb = async (kbUid: string, agentUid: string | null) => {
        if (!companyId) return;
        try {
            await setKnowledgeBaseAgent(companyId, kbUid, agentUid);
            showToast("Assistant rebound", 3000);
            reload();
        } catch {
            showToast("Failed to rebind", 3000);
        }
    };

    if (isLoading) return <LoaderView show />;

    return (
        <HeaderLayout blurHeader={false}>
            <div className="agents-admin">
                <div className="agents-admin-title">
                    <span className="agents-admin-title__text" role="heading" aria-level={1}>
                        Agents
                    </span>
                </div>
                <div className="agents-admin__flag">
                    <FormControlLabel
                        control={
                            <Switch
                                checked={flagEnabled}
                                onChange={e => onToggleFlag(e.target.checked)}
                            />
                        }
                        label="Agentic chat enabled for this company"
                    />
                </div>

                <Tabs value={tab} onChange={(_, v) => setTab(v)} className="agents-admin__tabs">
                    <Tab label={`Agents (${agents.length})`} />
                    <Tab label="Assistant bindings" />
                    <Tab label={`Flagged answers (${flaggedTotal})`} />
                </Tabs>

                {tab === 0 && (
                    <div className="agents-admin__list">
                        <div className="agents-admin__actions">
                            <Button
                                className="agents-admin-create-button"
                                variant="text"
                                startIcon={
                                    <SvgIcon
                                        className="agents-admin-create-button__icon"
                                        component={PlusIcon}
                                        inheritViewBox
                                    />
                                }
                                onClick={() => setSpecialistDialogOpen(true)}
                            >
                                Specialist from Assistant
                            </Button>
                            <Button
                                className="agents-admin-create-button"
                                variant="text"
                                startIcon={
                                    <SvgIcon
                                        className="agents-admin-create-button__icon"
                                        component={PlusIcon}
                                        inheritViewBox
                                    />
                                }
                                onClick={() => setOrchestratorDialogOpen(true)}
                            >
                                New Orchestrator
                            </Button>
                        </div>
                        <table className="agents-admin__table" aria-label="Agents">
                            <thead>
                                <tr>
                                    <th scope="col">Name</th>
                                    <th scope="col">Role</th>
                                    <th scope="col">Model</th>
                                    <th scope="col">Coverage</th>
                                    <th scope="col" aria-label="actions" />
                                </tr>
                            </thead>
                            <tbody>
                                {agents.map(a => (
                                    <tr key={a.uid}>
                                        <td>{a.name}</td>
                                        <td>
                                            <span className="agents-admin-badge">
                                                {a.role === "ORCHESTRATOR"
                                                    ? "Orchestrator"
                                                    : "Specialist"}
                                            </span>
                                        </td>
                                        <td>{modelSummary(a.modelPrimary)}</td>
                                        <td>
                                            {a.role === "ORCHESTRATOR"
                                                ? `${a.specialists.length} specialists`
                                                : a.assets.map(x => x.name).join(", ") || "—"}
                                        </td>
                                        <td className="agents-admin__table-actions">
                                            <Tooltip title="Edit Agent">
                                                <span>
                                                    <IconButton
                                                        className="agents-admin-action-button"
                                                        aria-label={`Edit Agent ${a.name}`}
                                                        onClick={() => setEditTarget(a)}
                                                    >
                                                        <SvgIcon
                                                            component={EditIcon}
                                                            inheritViewBox
                                                        />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            {a.assets.length === 0 &&
                                                a.boundKnowledgeBases.length === 0 && (
                                                    <Tooltip title="Delete Agent">
                                                        <span>
                                                            <IconButton
                                                                className="agents-admin-action-button"
                                                                aria-label={`Delete Agent ${a.name}`}
                                                                onClick={() => setDeleteTarget(a)}
                                                            >
                                                                <SvgIcon
                                                                    component={DeleteIcon}
                                                                    inheritViewBox
                                                                />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {tab === 1 && (
                    <table className="agents-admin__table" aria-label="Assistant bindings">
                        <thead>
                            <tr>
                                <th scope="col">Assistant</th>
                                <th scope="col">Serving agent</th>
                            </tr>
                        </thead>
                        <tbody>
                            {kbs.map(kb => (
                                <tr key={kb.uid}>
                                    <td>{kb.label}</td>
                                    <td>
                                        <FormControl size="small" fullWidth>
                                            <Select
                                                value={kb.agentUid ?? ""}
                                                displayEmpty
                                                inputProps={{
                                                    "aria-label": `Serving agent for ${kb.label}`
                                                }}
                                                onChange={e =>
                                                    onRebindKb(
                                                        kb.uid,
                                                        (e.target.value as string) || null
                                                    )
                                                }
                                            >
                                                <MenuItem value="">None (legacy path)</MenuItem>
                                                {agents.map(a => (
                                                    <MenuItem key={a.uid} value={a.uid}>
                                                        {a.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}

                {tab === 2 && (
                    <div className="agents-admin__flagged">
                        {flagged.length === 0 && <p>No flagged answers.</p>}
                        {flagged.map(f => (
                            <div key={f.id} className="agents-admin__flagged-item">
                                <div className="agents-admin__flagged-head">
                                    <span>{new Date(f.createdAt).toLocaleString()}</span>
                                    <span>{f.userEmail ?? "—"}</span>
                                    <span className="agents-admin__flagged-chips">
                                        {f.flags.bestEffort && (
                                            <em className="agents-admin-status agents-admin-status--orange">
                                                best-effort
                                            </em>
                                        )}
                                        {f.flags.failedOver && (
                                            <em className="agents-admin-status agents-admin-status--red">
                                                failed-over
                                            </em>
                                        )}
                                    </span>
                                    <span className="agents-admin__flagged-q">{f.question}</span>
                                    <IconButton
                                        size="small"
                                        aria-expanded={expandedFlag === f.id}
                                        aria-label={`Show trace for message ${f.id}`}
                                        onClick={() =>
                                            setExpandedFlag(expandedFlag === f.id ? null : f.id)
                                        }
                                    >
                                        {expandedFlag === f.id ? (
                                            <ExpandLessIcon />
                                        ) : (
                                            <ExpandMoreIcon />
                                        )}
                                    </IconButton>
                                </div>
                                <Collapse in={expandedFlag === f.id}>
                                    <div className="agents-admin__flagged-body">
                                        <p>
                                            <strong>Answer:</strong> {f.answer || "(empty)"}
                                        </p>
                                        <pre>{JSON.stringify(f.trace, null, 2)}</pre>
                                    </div>
                                </Collapse>
                            </div>
                        ))}
                    </div>
                )}

                <ConfirmationPopup
                    open={!!deleteTarget}
                    type={ConfirmationPopupType.dangerous}
                    title="Delete Agent?"
                    description={`"${deleteTarget?.name ?? ""}" is not related to any Assistant. Deleting it removes it from every orchestrator's specialist list and unbinds any surface still pointing at it. This cannot be undone.`}
                    actionName="Delete"
                    showCancelButton
                    handleAction={onDeleteAgent}
                    handleClose={() => setDeleteTarget(null)}
                />
                {editTarget && companyId && (
                    <AgentEditDialog
                        companyId={companyId}
                        agent={editTarget}
                        models={models}
                        specialists={specialists}
                        onClose={() => setEditTarget(null)}
                        onSaved={() => {
                            setEditTarget(null);
                            reload();
                        }}
                    />
                )}
                {specialistDialogOpen && companyId && (
                    <CreateSpecialistDialog
                        companyId={companyId}
                        kbs={kbs}
                        onClose={() => setSpecialistDialogOpen(false)}
                        onCreated={() => {
                            setSpecialistDialogOpen(false);
                            reload();
                        }}
                    />
                )}
                {orchestratorDialogOpen && companyId && (
                    <CreateOrchestratorDialog
                        companyId={companyId}
                        specialists={specialists}
                        onClose={() => setOrchestratorDialogOpen(false)}
                        onCreated={() => {
                            setOrchestratorDialogOpen(false);
                            reload();
                        }}
                    />
                )}
            </div>
        </HeaderLayout>
    );
}

function ModelSelect({
    label,
    value,
    models,
    onChange
}: {
    label: string;
    value: AgentModelRef | null;
    models: CuratedModel[];
    onChange: (value: AgentModelRef | null) => void;
}) {
    const selected = value ? models.find(m => modelKey(m) === modelKey(value)) : undefined;
    return (
        <div className="agents-admin__model-cell">
            <FormControl size="small" fullWidth margin="dense">
                <InputLabel>{label}</InputLabel>
                <Select
                    label={label}
                    value={modelKey(value)}
                    onChange={e => {
                        const v = e.target.value as string;
                        if (v === PLATFORM_DEFAULT) return onChange(null);
                        const found = models.find(m => modelKey(m) === v);
                        if (!found) return onChange(null);
                        const next: AgentModelRef = {
                            provider: found.provider,
                            model: found.model
                        };
                        if (found.supportsEffort && value?.effort) next.effort = value.effort;
                        onChange(next);
                    }}
                >
                    <MenuItem value={PLATFORM_DEFAULT}>Platform default</MenuItem>
                    {models.map(m => (
                        <MenuItem key={modelKey(m)} value={modelKey(m)}>
                            {m.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            {value && selected?.supportsEffort && (
                <FormControl size="small" fullWidth margin="dense">
                    <InputLabel>{`${label} — reasoning effort`}</InputLabel>
                    <Select
                        label={`${label} — reasoning effort`}
                        value={value.effort ?? PROVIDER_DEFAULT_EFFORT}
                        onChange={e => {
                            const v = e.target.value as string;
                            const next: AgentModelRef = {
                                provider: value.provider,
                                model: value.model
                            };
                            if (v !== PROVIDER_DEFAULT_EFFORT) next.effort = v as AgentEffortLevel;
                            onChange(next);
                        }}
                    >
                        <MenuItem value={PROVIDER_DEFAULT_EFFORT}>Provider default</MenuItem>
                        {EFFORT_OPTIONS.map(o => (
                            <MenuItem key={o.value} value={o.value}>
                                {o.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}
        </div>
    );
}

function AgentEditDialog({
    companyId,
    agent,
    models,
    specialists,
    onClose,
    onSaved
}: {
    companyId: number;
    agent: AdminAgent;
    models: CuratedModel[];
    specialists: AdminAgent[];
    onClose: () => void;
    onSaved: () => void;
}) {
    const { showToast } = ToastContainer.useContainer();
    const [persona, setPersona] = useState(agent.persona);
    const [modelPrimary, setModelPrimary] = useState<AgentModelRef | null>(agent.modelPrimary);
    const [modelSecondary, setModelSecondary] = useState<AgentModelRef | null>(
        agent.modelSecondary
    );
    const [budgets, setBudgets] = useState<Record<string, number>>({ ...agent.budgets });
    // P4-01: ask_user is live — enabled registers the tool at depth 0 and the
    // guidance is appended to its description in the agent loop.
    const [askEnabled, setAskEnabled] = useState<boolean>(!!agent.askPolicy?.enabled);
    const [askGuidance, setAskGuidance] = useState<string>(agent.askPolicy?.guidance ?? "");
    const [askMax, setAskMax] = useState<number | undefined>(
        agent.askPolicy?.maxPerConversation ?? undefined
    );
    // Phase B response dials — empty string = default behavior.
    const [verbosity, setVerbosity] = useState<string>(agent.responseOptions?.verbosity ?? "");
    const [language, setLanguage] = useState<string>(
        agent.responseOptions?.language && agent.responseOptions.language !== "auto"
            ? agent.responseOptions.language
            : ""
    );
    const [memberUids, setMemberUids] = useState<string[]>(agent.specialists.map(s => s.uid));
    const [saving, setSaving] = useState(false);
    const dialHelpId = useId();
    // Role-aware budget defaults (agentProvisioning + D9): orchestrators
    // provision at 6 iterations / 200k tokens.
    const budgetDefaults: Record<string, number> =
        agent.role === "ORCHESTRATOR"
            ? {
                  maxIterations: 6,
                  maxTokens: 200000,
                  llmCallTimeoutMs: 90000,
                  toolTimeoutMs: 30000,
                  turnDeadlineMs: 300000
              }
            : {
                  maxIterations: 8,
                  maxTokens: 60000,
                  llmCallTimeoutMs: 90000,
                  toolTimeoutMs: 30000,
                  turnDeadlineMs: 300000
              };
    const budgetDefaultText = (key: string): string | undefined =>
        key in budgetDefaults ? String(budgetDefaults[key]) : undefined;

    // D7: a specialist's persona is a derived copy of its Assistant's prompt +
    // Answer style (re-synced on every Assistant edit) — read-only here and
    // left out of the save; the server rejects direct edits.
    const personaDerived = agent.role !== "ORCHESTRATOR";

    const save = async () => {
        setSaving(true);
        try {
            await updateAgent(companyId, agent.uid, {
                ...(personaDerived ? {} : { persona }),
                modelPrimary,
                modelSecondary,
                // Backfill any blanked base budget with its role default so an
                // orchestrator never falls back to the flow's specialist
                // defaults (D9); the two nullable dials stay absent when unset.
                budgets: { ...budgetDefaults, ...budgets },
                askPolicy: {
                    ...agent.askPolicy,
                    enabled: askEnabled,
                    guidance: askGuidance.trim(),
                    // D10: enforced in the loop — the room's ask count gates
                    // ask_user registration once the cap is reached.
                    maxPerConversation: askMax ?? null
                },
                responseOptions:
                    verbosity || language
                        ? {
                              ...(verbosity ? { verbosity } : {}),
                              ...(language ? { language } : {})
                          }
                        : null
            });
            if (agent.role === "ORCHESTRATOR") {
                await setAgentSpecialists(companyId, agent.uid, memberUids);
            }
            showToast("Agent updated", 3000);
            onSaved();
        } catch (e: any) {
            showToast(e?.response?.data?.message ?? "Failed to update agent", 3000);
        } finally {
            setSaving(false);
        }
    };

    return (
        <ModalTemplate
            open
            width={900}
            ariaLabel={`Edit agent ${agent.name}`}
            handleClose={onClose}
        >
            <DialHelpProvider helpId={dialHelpId}>
                <div className="agents-admin-modal">
                    <h2 className="agents-admin-modal__title">Edit {agent.name}</h2>
                    <div className="agents-admin-modal__body">
                        <TextField
                            label="Persona"
                            value={persona}
                            onChange={e => setPersona(e.target.value)}
                            multiline
                            minRows={6}
                            fullWidth
                            margin="dense"
                            InputProps={{ readOnly: personaDerived }}
                            helperText={
                                personaDerived
                                    ? "Derived from the Assistant's prompt and Answer style — edit those in the Assistants view (kept in sync automatically)."
                                    : undefined
                            }
                        />
                        <div className="agents-admin__model-row">
                            <ModelSelect
                                label="Primary model"
                                value={modelPrimary}
                                models={models}
                                onChange={setModelPrimary}
                            />
                            <ModelSelect
                                label="Secondary model (failover)"
                                value={modelSecondary}
                                models={models}
                                onChange={setModelSecondary}
                            />
                        </div>
                        <div className="agents-admin__budget-row">
                            {BUDGET_FIELDS.map(f => (
                                <DialField
                                    key={f.key}
                                    dial={f.key}
                                    noLabel
                                    defaultOverride={budgetDefaultText(f.key)}
                                    changed={
                                        budgets[f.key] !== undefined &&
                                        budgets[f.key] !== budgetDefaults[f.key]
                                    }
                                    onReset={() =>
                                        setBudgets(prev => {
                                            const next = { ...prev };
                                            // Base budgets reset to the ROLE default value
                                            // (orchestrators run 6 / 200k, D9); the two
                                            // nullable dials reset to absent.
                                            if (f.key in budgetDefaults)
                                                next[f.key] = budgetDefaults[f.key];
                                            else delete next[f.key];
                                            return next;
                                        })
                                    }
                                >
                                    <TextField
                                        label={dialLabel(f.key, budgetDefaultText(f.key))}
                                        type="number"
                                        size="small"
                                        margin="dense"
                                        placeholder="Default"
                                        value={budgets[f.key] ?? ""}
                                        onChange={e => {
                                            const raw = e.target.value;
                                            setBudgets(prev => {
                                                const next = { ...prev };
                                                if (raw === "") delete next[f.key];
                                                else {
                                                    const v = Number(raw);
                                                    if (!isNaN(v)) next[f.key] = v;
                                                }
                                                return next;
                                            });
                                        }}
                                    />
                                </DialField>
                            ))}
                        </div>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={askEnabled}
                                    onChange={e => setAskEnabled(e.target.checked)}
                                />
                            }
                            label="Allow clarifying questions (ask_user)"
                        />
                        {askEnabled && (
                            <>
                                <TextField
                                    label="When to ask (guidance)"
                                    placeholder="Ask only when the answer depends on a choice the user has not made and the evidence supports more than one. Never ask when another search could settle it."
                                    value={askGuidance}
                                    onChange={e => setAskGuidance(e.target.value)}
                                    multiline
                                    minRows={2}
                                    fullWidth
                                    margin="dense"
                                />
                                <DialField
                                    dial="askMaxPerConversation"
                                    noLabel
                                    changed={askMax !== undefined}
                                    onReset={() => setAskMax(undefined)}
                                >
                                    <TextField
                                        label={dialLabel("askMaxPerConversation")}
                                        placeholder="Unlimited"
                                        type="number"
                                        size="small"
                                        margin="dense"
                                        fullWidth
                                        inputProps={{ min: 1, max: 20 }}
                                        value={askMax ?? ""}
                                        onChange={e => {
                                            const raw = e.target.value;
                                            if (raw === "") setAskMax(undefined);
                                            else {
                                                const v = parseInt(raw, 10);
                                                if (!isNaN(v)) setAskMax(v);
                                            }
                                        }}
                                    />
                                </DialField>
                            </>
                        )}
                        <div className="agents-admin__model-row">
                            <DialField
                                dial="verbosity"
                                noLabel
                                changed={verbosity !== ""}
                                onReset={() => setVerbosity("")}
                            >
                                <FormControl size="small" fullWidth margin="dense">
                                    <InputLabel>{dialLabel("verbosity")}</InputLabel>
                                    <Select
                                        label={dialLabel("verbosity")}
                                        value={verbosity}
                                        onChange={e => setVerbosity(e.target.value as string)}
                                    >
                                        {VERBOSITY_OPTIONS.map(o => (
                                            <MenuItem key={o.value} value={o.value}>
                                                {o.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </DialField>
                            <DialField
                                dial="language"
                                noLabel
                                changed={language !== ""}
                                onReset={() => setLanguage("")}
                            >
                                <TextField
                                    label={dialLabel("language")}
                                    placeholder="e.g. en, es, pt-BR"
                                    size="small"
                                    margin="dense"
                                    fullWidth
                                    value={language}
                                    onChange={e => setLanguage(e.target.value.trim())}
                                />
                            </DialField>
                        </div>
                        {agent.role === "ORCHESTRATOR" && (
                            <div className="agents-admin__members">
                                <p>
                                    <strong>Registered specialists</strong>
                                </p>
                                {specialists.map(s => (
                                    <FormControlLabel
                                        key={s.uid}
                                        control={
                                            <Checkbox
                                                checked={memberUids.includes(s.uid)}
                                                onChange={e =>
                                                    setMemberUids(prev =>
                                                        e.target.checked
                                                            ? [...prev, s.uid]
                                                            : prev.filter(u => u !== s.uid)
                                                    )
                                                }
                                            />
                                        }
                                        label={s.name}
                                    />
                                ))}
                                {specialists.length === 0 && <p>No specialists exist yet.</p>}
                            </div>
                        )}
                    </div>
                    <DialHelpStrip />
                    <div className="agents-admin-modal__actions">
                        <Button className="agents-admin-modal__cancel" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button
                            className="agents-admin-modal__submit"
                            onClick={save}
                            disabled={saving}
                        >
                            Save
                        </Button>
                    </div>
                </div>
            </DialHelpProvider>
        </ModalTemplate>
    );
}

function CreateSpecialistDialog({
    companyId,
    kbs,
    onClose,
    onCreated
}: {
    companyId: number;
    kbs: KbRow[];
    onClose: () => void;
    onCreated: () => void;
}) {
    const { showToast } = ToastContainer.useContainer();
    const [kbUid, setKbUid] = useState("");
    const [saving, setSaving] = useState(false);

    const create = async () => {
        if (!kbUid) return;
        setSaving(true);
        try {
            await createSpecialist(companyId, kbUid);
            showToast("Specialist provisioned", 3000);
            onCreated();
        } catch {
            showToast("Failed to provision specialist", 3000);
        } finally {
            setSaving(false);
        }
    };

    return (
        <ModalTemplate
            open
            width={560}
            ariaLabel="Create specialist from Assistant"
            handleClose={onClose}
        >
            <div className="agents-admin-modal">
                <h2 className="agents-admin-modal__title">Specialist from Assistant</h2>
                <FormControl size="small" fullWidth margin="dense">
                    <InputLabel>Assistant</InputLabel>
                    <Select
                        label="Assistant"
                        value={kbUid}
                        onChange={e => setKbUid(e.target.value as string)}
                    >
                        {kbs.map(kb => (
                            <MenuItem key={kb.uid} value={kb.uid}>
                                {kb.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <p className="agents-admin__hint">
                    Provisions (or re-syncs) the specialist agent for this Assistant and binds it.
                </p>
                <div className="agents-admin-modal__actions">
                    <Button className="agents-admin-modal__cancel" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        className="agents-admin-modal__submit"
                        onClick={create}
                        disabled={!kbUid || saving}
                    >
                        Create
                    </Button>
                </div>
            </div>
        </ModalTemplate>
    );
}

function CreateOrchestratorDialog({
    companyId,
    specialists,
    onClose,
    onCreated
}: {
    companyId: number;
    specialists: AdminAgent[];
    onClose: () => void;
    onCreated: () => void;
}) {
    const { showToast } = ToastContainer.useContainer();
    const [name, setName] = useState("");
    const [memberUids, setMemberUids] = useState<string[]>([]);
    const [saving, setSaving] = useState(false);

    const create = async () => {
        if (!name.trim()) return;
        setSaving(true);
        try {
            await createOrchestrator(companyId, {
                name: name.trim(),
                specialistUids: memberUids
            });
            showToast("Orchestrator created", 3000);
            onCreated();
        } catch (e: any) {
            showToast(e?.response?.data?.message ?? "Failed to create orchestrator", 3000);
        } finally {
            setSaving(false);
        }
    };

    return (
        <ModalTemplate open width={560} ariaLabel="Create orchestrator" handleClose={onClose}>
            <div className="agents-admin-modal">
                <h2 className="agents-admin-modal__title">New orchestrator</h2>
                <TextField
                    label="Name"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    fullWidth
                    margin="dense"
                />
                <p>
                    <strong>Register specialists</strong>
                </p>
                {specialists.map(s => (
                    <FormControlLabel
                        key={s.uid}
                        control={
                            <Checkbox
                                checked={memberUids.includes(s.uid)}
                                onChange={e =>
                                    setMemberUids(prev =>
                                        e.target.checked
                                            ? [...prev, s.uid]
                                            : prev.filter(u => u !== s.uid)
                                    )
                                }
                            />
                        }
                        label={s.name}
                    />
                ))}
                <div className="agents-admin-modal__actions">
                    <Button className="agents-admin-modal__cancel" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        className="agents-admin-modal__submit"
                        onClick={create}
                        disabled={!name.trim() || saving}
                    >
                        Create
                    </Button>
                </div>
            </div>
        </ModalTemplate>
    );
}
